/**
 * `LineAreaChart` — generic inline-SVG line + filled-area chart for
 * the admin metrics time-series strip.
 *
 * Modeled on the dashboard's `TrafficChart` (`apps/dashboard/src/views/
 * dashboard/Dashboard.tsx`): one or more series sharing a y-scale, an
 * area fill with a gradient, a stroked line on top, optional hover
 * cursor + tooltip, mono x-tick labels, mono y-gridlines.
 *
 * Multi-series:
 *   - Pass `series: [{ key, label, color, accessor }, ...]`.
 *   - The first series renders an area fill; subsequent series render
 *     stroked lines only (keeps the chart legible — overlapping fills
 *     get visually noisy at 4+ currencies).
 *
 * `data` is the BACKEND's series (zero-filled gaps — the API already
 * does this per `AdminMetricsOverview.series`). `xLabels` is the
 * per-bucket x-tick label list (formatted ET dates).
 *
 * Empty / loading / error are caller-driven — the chart never decides
 * what to show on a fetch failure; the section renders an empty/error
 * card instead.
 */

import { useMemo, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';

import {
  buildSeriesGeometry,
  niceGridlines,
  thinAxisLabels,
} from '../../lib/chart-geometry.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface LineSeries<T> {
  /** Stable key — used as React key + SVG gradient id suffix. */
  key: string;
  /** Display label in the legend + tooltip. */
  label: string;
  /** Hex / token color for the line stroke + (first series) area
   *  gradient. */
  color: string;
  /** Read a numeric value out of one data point. Return 0 for
   *  null/undefined-y data; the chart treats every value as a number. */
  accessor: (point: T) => number;
  /** When true, render a filled area under the line (gradient → 0).
   *  When false, line-only. Default: true for the first series, false
   *  for the rest. */
  fill?: boolean;
  /** Optional value formatter for the tooltip (`12,345` vs `$12.34`).
   *  Defaults to locale-grouped integer. */
  formatTooltip?: (v: number) => string;
}

export interface LineAreaChartProps<T> {
  data: ReadonlyArray<T>;
  xLabels: ReadonlyArray<string>;
  series: ReadonlyArray<LineSeries<T>>;
  /** Optional formatter for y-gridline labels (default: locale int). */
  formatY?: (v: number) => string;
  /** Optional aria-label for the SVG (a11y). */
  ariaLabel?: string;
  /** Render an empty-state node when `data` is empty OR every series
   *  is all-zero. Caller-controlled wording. */
  emptyState?: ReactNode;
  /** Visible height in px (the SVG viewBox is fixed; the px height
   *  controls the rendered size in the layout). Default 220. */
  height?: number;
}

export function LineAreaChart<T>({
  data,
  xLabels,
  series,
  formatY,
  ariaLabel,
  emptyState,
  height = 220,
}: LineAreaChartProps<T>): JSX.Element {
  const C = useC();

  const isEmpty =
    data.length === 0 ||
    series.every((s) =>
      data.every((p) => {
        const v = s.accessor(p);
        return !Number.isFinite(v) || v === 0;
      }),
    );

  const accessors = useMemo(
    () => series.map((s) => s.accessor),
    [series],
  );
  const geo = useMemo(
    () => buildSeriesGeometry(data, accessors),
    [data, accessors],
  );

  const gridValues = useMemo(() => niceGridlines(geo.maxY, 4), [geo.maxY]);

  const ticks = useMemo(() => thinAxisLabels(xLabels, 5), [xLabels]);

  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  if (isEmpty && emptyState !== undefined) {
    return <div role="img" aria-label={ariaLabel}>{emptyState}</div>;
  }

  const handleMove = (e: ReactPointerEvent<SVGRectElement>): void => {
    if (data.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width === 0) return;
    const svgX = ((e.clientX - rect.left) / rect.width) * geo.width;
    let best = 0;
    let bestDist = Math.abs(geo.xAt(0) - svgX);
    for (let i = 1; i < data.length; i += 1) {
      const d = Math.abs(geo.xAt(i) - svgX);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    setHoverIdx(best);
  };
  const handleLeave = (): void => setHoverIdx(null);

  const hovered = hoverIdx !== null && hoverIdx < data.length
    ? (data[hoverIdx] as T)
    : null;
  const hoverPct =
    hoverIdx !== null ? (geo.xAt(hoverIdx) / geo.width) * 100 : 0;

  const fmtY = formatY ?? ((v: number) => Math.round(v).toLocaleString('en-US'));

  return (
    <div style={{ position: 'relative' }}>
      <div className="flex" style={{ alignItems: 'stretch' }}>
        {/* Y-axis label gutter (left side). */}
        <div
          className="flex flex-col justify-between flex-shrink-0"
          style={{
            width: 56,
            height,
            paddingRight: 8,
            paddingTop: 2,
            paddingBottom: 14,
            textAlign: 'right',
          }}
        >
          {[...gridValues].reverse().map((v, i) => (
            <span
              key={`y-${i}`}
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.dim,
                letterSpacing: 0.5,
                lineHeight: 1,
              }}
            >
              {fmtY(v)}
            </span>
          ))}
        </div>

        {/* The chart pane itself. */}
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <svg
            width="100%"
            height={height}
            viewBox={`0 0 ${geo.width} ${geo.height}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={ariaLabel}
            style={{ display: 'block' }}
          >
            <defs>
              {series.map((s) => {
                const id = `metricsGrad-${s.key}`;
                return (
                  <linearGradient
                    key={s.key}
                    id={id}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={s.color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                  </linearGradient>
                );
              })}
            </defs>

            {/* Horizontal gridlines. */}
            {gridValues.map((v, i) => {
              const y = geo.yAt(v);
              return (
                <line
                  key={`grid-${i}`}
                  x1={0}
                  x2={geo.width}
                  y1={y}
                  y2={y}
                  stroke={C.borderSoft}
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}

            {/* Area fills (first series fills by default; others
                opt in via `fill: true`). */}
            {series.map((s, i) => {
              const filled = s.fill ?? i === 0;
              if (!filled) return null;
              return (
                <path
                  key={`area-${s.key}`}
                  d={geo.areaFor(s.accessor)}
                  fill={`url(#metricsGrad-${s.key})`}
                  stroke="none"
                />
              );
            })}

            {/* Line strokes. */}
            {series.map((s, i) => (
              <path
                key={`line-${s.key}`}
                d={geo.lineFor(s.accessor)}
                fill="none"
                stroke={s.color}
                strokeWidth={i === 0 ? 2 : 1.5}
                vectorEffect="non-scaling-stroke"
              />
            ))}

            {/* Hover cursor + markers. */}
            {hovered !== null && hoverIdx !== null ? (
              <g>
                <line
                  x1={geo.xAt(hoverIdx)}
                  x2={geo.xAt(hoverIdx)}
                  y1={0}
                  y2={geo.height}
                  stroke={C.muted}
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  vectorEffect="non-scaling-stroke"
                  opacity={0.6}
                />
                {series.map((s) => (
                  <circle
                    key={`marker-${s.key}`}
                    cx={geo.xAt(hoverIdx)}
                    cy={geo.yAt(s.accessor(hovered))}
                    r={3}
                    fill={s.color}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
              </g>
            ) : null}

            {/* Invisible pointer overlay. */}
            <rect
              x={0}
              y={0}
              width={geo.width}
              height={geo.height}
              fill="transparent"
              onPointerMove={handleMove}
              onPointerLeave={handleLeave}
              style={{ cursor: 'crosshair' }}
            />
          </svg>

          {/* Hover tooltip — absolute, HTML, anchored to chart pane. */}
          {hovered !== null && hoverIdx !== null ? (
            <div
              role="tooltip"
              className="pointer-events-none absolute"
              style={{
                left: `${hoverPct}%`,
                top: 0,
                transform: 'translate(-50%, -100%)',
                background: C.surface3,
                border: `1px solid ${C.border}`,
                borderRadius: 3,
                padding: '6px 10px',
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
                whiteSpace: 'nowrap',
                minWidth: 140,
                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                zIndex: 5,
              }}
            >
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.muted,
                  letterSpacing: 1,
                  marginBottom: 4,
                  textTransform: 'uppercase',
                }}
              >
                {xLabels[hoverIdx] ?? ''}
              </div>
              {series.map((s) => {
                const v = s.accessor(hovered);
                const fmt = s.formatTooltip ?? ((x: number) =>
                  Math.round(x).toLocaleString('en-US'));
                return (
                  <div
                    key={`tt-${s.key}`}
                    className="flex items-center justify-between gap-3"
                  >
                    <span
                      style={{
                        fontFamily: monoFont,
                        fontSize: 10,
                        color: s.color,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      {s.label}
                    </span>
                    <span
                      style={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        color: C.text,
                      }}
                    >
                      {fmt(v)}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}

          {/* X-axis tick labels under the chart. */}
          <div
            className="flex justify-between"
            style={{ marginTop: 6, paddingLeft: 0, paddingRight: 0 }}
          >
            {ticks.map((label, i) => (
              <span
                key={`x-${i}`}
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.dim,
                  letterSpacing: 0.5,
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
